import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useGoogleSheets } from './hooks/useGoogleSheets';
import { googleSheetsConfig } from './config/googleSheets';
import { mockKPIMetrics, mockTeams, mockEmployeesTable } from './data/mockData';
import { Team } from './types';
import { YearlyChart } from './components/YearlyChart';
import { ResourcePlanningGauges } from './components/GaugeChart';
import { AnnouncementsWidget } from './components/AnnouncementsWidget';
import { FreshdeskTicketsWidget } from './components/FreshdeskTicketsWidget';
import { useAuth } from './contexts/AuthContext';
import UserInfo from './components/UserInfo';
import { OverallPerformanceWidget } from './components/widgets/OverallPerformanceWidget';
import { TeamKPIsWidget } from './components/widgets/TeamKPIsWidget';
import { TeamMembersWidget } from './components/widgets/TeamMembersWidget';

interface YearlyData {
  month: string;
  monthName: string;
  budgetTotalMonth: number;
  actualAsOfYesterday: number;
}

interface ResourcePlanningData {
  team: string;
  billableRateThisWeek: number;
  billableRateNextWeek: number;
  billableRateIn2Weeks: number;
  billableRateIn3Weeks: number;
}

const App: React.FC = () => {
  // Filter states - now arrays to handle multiple selections
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [selectedHorizontalTeams, setSelectedHorizontalTeams] = useState<string[]>([]);

  // Get current month as default (format: '01-2025', '02-2025', etc.)
  const getCurrentMonth = () => {
    const now = new Date();
    const month = now.getMonth() + 1; // getMonth() returns 0-11, so add 1
    const year = now.getFullYear();
    return `${month.toString().padStart(2, '0')}-${year}`; // Format: "01-2025"
  };

  const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonth());
  const [teamData, setTeamData] = useState<Team[]>([]);
  const [yearlyData, setYearlyData] = useState<YearlyData[]>([]);
  const [isLoadingYearlyData, setIsLoadingYearlyData] = useState(false);
  const [resourcePlanningData, setResourcePlanningData] = useState<ResourcePlanningData[]>([]);
  const [isLoadingResourcePlanning, setIsLoadingResourcePlanning] = useState(false);

  // Announcements state
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [isLoadingAnnouncements, setIsLoadingAnnouncements] = useState(false);

  // Freshdesk tickets state
  const [freshdeskTickets, setFreshdeskTickets] = useState<any[]>([]);
  const [isLoadingFreshdeskTickets, setIsLoadingFreshdeskTickets] = useState(false);

  // Refs for measuring heights
  const overallPerformanceRef = useRef<HTMLDivElement>(null);
  const resourcePlanningRef = useRef<HTMLDivElement>(null);
  const [announcementsHeight, setAnnouncementsHeight] = useState<number | null>(null);
  const teamKPIsRef = useRef<HTMLDivElement>(null);
  const teamMembersRef = useRef<HTMLDivElement>(null);

  const {
    kpiMetrics, teams, design, employeesTable,
    isLoading, error, backendStatus, setKpiMetrics
  } = useGoogleSheets(googleSheetsConfig, selectedTeams, selectedHorizontalTeams, selectedMonth);

  const { user } = useAuth();

  // Helper function to get API URL
  const getApiUrl = () => {
    if (!process.env.REACT_APP_API_URL) {
      throw new Error('REACT_APP_API_URL environment variable is required');
    }
    return process.env.REACT_APP_API_URL;
  };

  // Fetch team-specific data when filters change
  useEffect(() => {
    if (backendStatus === 'online') {
      const fetchTeamData = async () => {
        try {
          const queryParams = new URLSearchParams();
          queryParams.append('month', selectedMonth);
          if (selectedTeams.length > 0) {
            queryParams.append('team', selectedTeams.join(','));
          }
          if (selectedHorizontalTeams.length > 0) {
            queryParams.append('horizontalTeam', selectedHorizontalTeams.join(','));
          }

          const queryString = queryParams.toString();
          const endpoint = `/api/teams-data?${queryString}`;

          const response = await fetch(`${getApiUrl()}${endpoint}`);
          if (!response.ok) {
            throw new Error(`Backend API error: ${response.status} ${response.statusText}`);
          }

          const data = await response.json();
          setTeamData(data);
        } catch (err) {
          console.error('Failed to fetch team data:', err);
          setTeamData(mockTeams);
        }
      };

      fetchTeamData();
    }
  }, [selectedMonth, selectedTeams, selectedHorizontalTeams, backendStatus]);

  // Fetch KPI data when filters change
  useEffect(() => {
    if (backendStatus === 'online') {
      const refreshWithFilters = async () => {
        try {
          // Build query string for filters
          const queryParams = new URLSearchParams();
          if (selectedTeams.length > 0) queryParams.append('team', selectedTeams.join(','));
          if (selectedHorizontalTeams.length > 0) queryParams.append('horizontalTeam', selectedHorizontalTeams.join(','));
          // Always include month parameter
          queryParams.append('month', selectedMonth);

          const queryString = queryParams.toString();
          const endpoint = `/api/kpi?${queryString}`;

          const response = await fetch(`${getApiUrl()}${endpoint}`);
          if (!response.ok) {
            throw new Error(`Backend API error: ${response.status} ${response.statusText}`);
          }

          const data = await response.json();
          console.log('KPI data received:', data);
          // Ensure all values are numbers, not null/undefined
          const sanitizedData = {
            budgetTotalMonth: data.budgetTotalMonth ?? 0,
            budgetAsOfYesterday: data.budgetAsOfYesterday ?? 0,
            actualAsOfYesterday: data.actualAsOfYesterday ?? 0,
            billableRateSoFar: data.billableRateSoFar ?? 0,
            budgetBillableRate: data.budgetBillableRate ?? 0,
            expectedTotalHours: data.expectedTotalHours ?? 0,
            loggedTotalHours: data.loggedTotalHours ?? 0,
            absence: data.absence ?? 0,
            vacation: data.vacation ?? 0,
          };
          setKpiMetrics(sanitizedData);
        } catch (err) {
          console.error('Failed to refresh KPI data:', err);
          // Don't set to null, keep existing data or use mock
        }
      };

      refreshWithFilters();
    }
  }, [selectedTeams, selectedHorizontalTeams, selectedMonth, backendStatus, setKpiMetrics]);

  // Initial KPI data fetch when app starts
  useEffect(() => {
    if (backendStatus === 'online' && !kpiMetrics) {
      const fetchInitialKPIData = async () => {
        try {
          const queryParams = new URLSearchParams();
          queryParams.append('month', selectedMonth);

          const queryString = queryParams.toString();
          const endpoint = `/api/kpi?${queryString}`;

          const response = await fetch(`${getApiUrl()}${endpoint}`);
          if (!response.ok) {
            throw new Error(`Backend API error: ${response.status} ${response.statusText}`);
          }

          const data = await response.json();
          console.log('Initial KPI data received:', data);
          // Ensure all values are numbers, not null/undefined
          const sanitizedData = {
            budgetTotalMonth: data.budgetTotalMonth ?? 0,
            budgetAsOfYesterday: data.budgetAsOfYesterday ?? 0,
            actualAsOfYesterday: data.actualAsOfYesterday ?? 0,
            billableRateSoFar: data.billableRateSoFar ?? 0,
            budgetBillableRate: data.budgetBillableRate ?? 0,
            expectedTotalHours: data.expectedTotalHours ?? 0,
            loggedTotalHours: data.loggedTotalHours ?? 0,
            absence: data.absence ?? 0,
            vacation: data.vacation ?? 0,
          };
          setKpiMetrics(sanitizedData);
        } catch (err) {
          console.error('Failed to fetch initial KPI data:', err);
          // Don't set to null, keep existing data or use mock
        }
      };

      fetchInitialKPIData();
    }
  }, [backendStatus, selectedMonth, kpiMetrics, setKpiMetrics]);

  // Fetch yearly data when "all" is selected
  useEffect(() => {
    if (backendStatus === 'online' && selectedMonth === 'all') {
      const fetchYearlyData = async () => {
        setIsLoadingYearlyData(true);
        try {
          const queryParams = new URLSearchParams();
          if (selectedTeams.length > 0) queryParams.append('team', selectedTeams.join(','));
          if (selectedHorizontalTeams.length > 0) queryParams.append('horizontalTeam', selectedHorizontalTeams.join(','));

          const queryString = queryParams.toString();
          const endpoint = `/api/yearly-data?${queryString}`;

          const response = await fetch(`${getApiUrl()}${endpoint}`);
          if (!response.ok) {
            throw new Error(`Backend API error: ${response.status} ${response.statusText}`);
          }

          const data = await response.json();
          setYearlyData(data);
        } catch (err) {
          console.error('Failed to fetch yearly data:', err);
          setYearlyData([]);
        } finally {
          setIsLoadingYearlyData(false);
        }
      };

      fetchYearlyData();
    } else {
      setYearlyData([]);
    }
  }, [backendStatus, selectedMonth, selectedTeams, selectedHorizontalTeams]);

  // Fetch resource planning data when teams are selected
  useEffect(() => {
    if (backendStatus === 'online') {
      const fetchResourcePlanningData = async () => {
        setIsLoadingResourcePlanning(true);
        try {
          const queryParams = new URLSearchParams();
          if (selectedTeams.length > 0) queryParams.append('team', selectedTeams.join(','));
          if (selectedHorizontalTeams.length > 0) queryParams.append('horizontalTeam', selectedHorizontalTeams.join(','));

          const queryString = queryParams.toString();
          const endpoint = `/api/resource-planning?${queryString}`;

          const response = await fetch(`${getApiUrl()}${endpoint}`);
          if (!response.ok) {
            throw new Error(`Backend API error: ${response.status} ${response.statusText}`);
          }

          const data = await response.json();
          setResourcePlanningData(data);
        } catch (err) {
          console.error('Failed to fetch resource planning data:', err);
          setResourcePlanningData([]);
        } finally {
          setIsLoadingResourcePlanning(false);
        }
      };

      fetchResourcePlanningData();
    } else {
      setResourcePlanningData([]);
    }
  }, [backendStatus, selectedTeams, selectedHorizontalTeams]);

  // Fetch announcements data
  useEffect(() => {
    if (backendStatus === 'online') {
      const fetchAnnouncements = async () => {
        setIsLoadingAnnouncements(true);
        try {
          const response = await fetch(`${getApiUrl()}/api/announcements`);
          if (!response.ok) {
            throw new Error(`Backend API error: ${response.status} ${response.statusText}`);
          }
          const data = await response.json();
          setAnnouncements(data);
        } catch (err) {
          console.error('Failed to fetch announcements:', err);
          setAnnouncements([]);
        } finally {
          setIsLoadingAnnouncements(false);
        }
      };

      fetchAnnouncements();
    } else {
      setAnnouncements([]);
    }
  }, [backendStatus]);

  // Fetch Freshdesk tickets for logged in user
  useEffect(() => {
    if (backendStatus === 'online' && user?.email) {
      const fetchFreshdeskTickets = async () => {
        setIsLoadingFreshdeskTickets(true);
        try {
          const response = await fetch(`${getApiUrl()}/api/freshdesk/tickets?email=${encodeURIComponent(user.email)}`);
          if (!response.ok) {
            throw new Error(`Backend API error: ${response.status} ${response.statusText}`);
          }
          const data = await response.json();
          setFreshdeskTickets(data);
        } catch (err) {
          console.error('Failed to fetch Freshdesk tickets:', err);
          setFreshdeskTickets([]);
        } finally {
          setIsLoadingFreshdeskTickets(false);
        }
      };

      fetchFreshdeskTickets();
    } else {
      setFreshdeskTickets([]);
    }
  }, [backendStatus, user?.email]);

  // Calculate announcements height based on left side boxes
  useEffect(() => {
    const calculateHeight = () => {
      if (overallPerformanceRef.current && resourcePlanningRef.current) {
        const overallHeight = overallPerformanceRef.current.offsetHeight;
        const resourceHeight = resourcePlanningRef.current.offsetHeight;
        const gap = 32; // mb-8 = 32px (2rem)
        const totalHeight = overallHeight + resourceHeight + gap;
        setAnnouncementsHeight(totalHeight);
      }
    };

    // Calculate after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(calculateHeight, 100);

    // Recalculate on window resize
    window.addEventListener('resize', calculateHeight);

    // Recalculate when data changes
    if (!isLoading && !isLoadingResourcePlanning) {
      calculateHeight();
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculateHeight);
    };
  }, [isLoading, isLoadingResourcePlanning, resourcePlanningData]);


  // Generate month options for all months in 2025 and 2026
  const monthOptions = useMemo(() => {
    const months = [
      { value: 'all', label: 'Hele året' }
    ];

    const monthNames = [
      'Januar', 'Februar', 'Mars', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Desember'
    ];

    // Add all months for 2025 and 2026
    for (let year = 2025; year <= 2026; year++) {
      for (let month = 0; month < 12; month++) {
        const monthValue = (month + 1).toString().padStart(2, '0');
        months.push({
          value: `${monthValue}-${year}`, // Format: "01-2025"
          label: `${monthNames[month]} ${year}`
        });
      }
    }

    return months;
  }, []);

  // Filter teams for display based on selected teams
  const currentTeams = useMemo(() => {
    if (selectedTeams.length === 0) {
      return teamData;
    }
    return teamData.filter(team => selectedTeams.includes(team.name));
  }, [teamData, selectedTeams]);

  // Filter and sort teams to match desired list
  const filteredAndSortedTeams = useMemo(() => {
    const desiredTeams = [
      'Team 1',
      'Team 2',
      'CSC',
      'Tech board',
      'Administration',
      'Team Litium',
      'Sweden / Litium',
      'Mediebruket'
    ];

    // Filter teams to only include desired ones, in the specified order
    const filtered = desiredTeams.filter(desiredTeam =>
      teams.includes(desiredTeam)
    );

    return filtered;
  }, [teams]);

  // Filter and sort horizontal teams to match desired list
  const filteredAndSortedHorizontalTeams = useMemo(() => {
    const desiredHorizontalTeams = [
      'E-commerce manager',
      'Administration',
      'Project manager',
      'Design',
      'WooCommerce Developer',
      'Integration developer',
      'Tech board',
      'Growth',
      'Litium Developer'
    ];

    // Filter horizontal teams to only include desired ones, in the specified order
    // Exclude "Horizontal team" as it's just a header
    const filtered = desiredHorizontalTeams.filter(desiredTeam =>
      design.includes(desiredTeam) && desiredTeam !== 'Horizontal team'
    );

    return filtered;
  }, [design]);

  // Filter employees based on selected teams
  // Employees table is already filtered by backend based on selectedTeams and selectedHorizontalTeams
  // So we just use the data directly from employeesTable
  const currentEmployees = useMemo(() => {
    return employeesTable.length > 0 ? employeesTable : mockEmployeesTable;
  }, [employeesTable]);

  // Filter KPI metrics based on selected filters
  const filteredKPIMetrics = useMemo(() => {
    return kpiMetrics || mockKPIMetrics;
  }, [kpiMetrics]);

  const getStatusText = (status: string) => {
    switch (status) {
      case 'online': return 'Backend Online';
      case 'offline': return 'Backend Offline';
      case 'checking': return 'Sjekker Backend...';
      default: return 'Ukjent Status';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200/60 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                    Dashboard
                  </h1>
                  <p className="text-sm text-slate-600 font-medium">Maksimer KPI and financial performance indicators</p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Backend Status - Only show when offline */}
              {backendStatus === 'offline' && (
                <div className={`px-3 py-2 rounded-lg text-sm font-medium ${
                  backendStatus === 'offline' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {getStatusText(backendStatus)}
                </div>
              )}

              {/* User Info and Logout */}
              {user && <UserInfo />}
            </div>
          </div>
        </div>
      </header>

      {/* Filters Section - Modern & Compact */}
      <div className="bg-gradient-to-r from-slate-50/80 to-blue-50/40 backdrop-blur-sm border-b border-slate-200/40">
        <div className="max-w-7xl mx-auto px-4 py-2.5">
          <div className="flex flex-wrap items-center gap-3">
            {/* Team Filter - Chip-based */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-600 whitespace-nowrap">Team:</span>
              <div className="flex flex-wrap gap-1.5">
                {filteredAndSortedTeams.map((team) => {
                  const isSelected = selectedTeams.includes(team);
                  return (
                    <button
                      key={team}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedTeams(selectedTeams.filter(t => t !== team));
                        } else {
                          setSelectedTeams([...selectedTeams, team]);
                        }
                      }}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all duration-200 ${
                        isSelected
                          ? 'bg-blue-600 text-white shadow-sm hover:bg-blue-700'
                          : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 hover:border-slate-400'
                      }`}
                    >
                      {team}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Divider */}
            <div className="w-px h-6 bg-slate-300"></div>

            {/* Horizontal Team Filter - Chip-based */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-600 whitespace-nowrap">Horisontal team:</span>
              <div className="flex flex-wrap gap-1.5">
                {filteredAndSortedHorizontalTeams.map((team) => {
                  const isSelected = selectedHorizontalTeams.includes(team);
                  return (
                    <button
                      key={team}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedHorizontalTeams(selectedHorizontalTeams.filter(t => t !== team));
                        } else {
                          setSelectedHorizontalTeams([...selectedHorizontalTeams, team]);
                        }
                      }}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all duration-200 ${
                        isSelected
                          ? 'bg-indigo-600 text-white shadow-sm hover:bg-indigo-700'
                          : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 hover:border-slate-400'
                      }`}
                    >
                      {team}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Divider */}
            <div className="w-px h-6 bg-slate-300"></div>

            {/* Month Filter - Compact */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-600 whitespace-nowrap">Måned:</span>
              <select
                id="month-select"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-2.5 py-1 text-xs bg-white border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 text-slate-700 font-medium"
              >
                {monthOptions.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Error Display */}
        {error && (
          <div className="mb-8 bg-red-50/80 backdrop-blur-sm border border-red-200/60 rounded-2xl p-6 shadow-lg">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="h-6 w-6 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-red-800">Feil ved lasting av data</h3>
                <div className="mt-1 text-red-700 font-medium">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content: Two Column Layout */}
        <div className="flex gap-6 mb-8 items-stretch">
          {/* Left Column: Overall Performance, Resource Planning, Team KPIs, Team Members (80% width) */}
          <div className="flex-1" style={{ width: '80%' }}>
            {/* Overall Performance - KPI Cards */}
            <div ref={overallPerformanceRef} className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/40 p-6 mb-8">
              <h2 className="text-xl font-semibold text-slate-800 mb-6">Overall performance</h2>
              <OverallPerformanceWidget kpiMetrics={filteredKPIMetrics} isLoading={isLoading} />
            </div>

            {/* Resource Planning Gauges */}
            <div ref={resourcePlanningRef} className="mb-8">
              <ResourcePlanningGauges
                data={resourcePlanningData}
                isLoading={isLoadingResourcePlanning}
              />
            </div>

            {/* Yearly Chart - Only show when "all" is selected */}
            {selectedMonth === 'all' && (
              <div className="mb-8">
                <YearlyChart data={yearlyData} isLoading={isLoadingYearlyData} />
              </div>
            )}

            {/* Team KPIs Table */}
            <div ref={teamKPIsRef} className="mb-8">
              <TeamKPIsWidget
                teams={currentTeams}
                employeesTable={employeesTable}
                resourcePlanningData={resourcePlanningData}
                isLoading={isLoading}
                onTeamSelect={(teamName) => {
                  if (selectedTeams.length === 1 && selectedTeams[0] === teamName) {
                    setSelectedTeams([]);
                  } else {
                    setSelectedTeams([teamName]);
                  }
                }}
                selectedTeams={selectedTeams}
              />
            </div>

            {/* Team Members Table */}
            <div ref={teamMembersRef}>
              <TeamMembersWidget employees={currentEmployees} isLoading={isLoading} />
            </div>
          </div>

          {/* Right Column: Announcements and My Tickets (20% width) */}
          <div style={{ width: '20%', flexShrink: 0 }} className="flex flex-col h-full">
            {/* Announcements Widget */}
            <div className="mb-6">
              <AnnouncementsWidget
                announcements={announcements}
                isLoading={isLoadingAnnouncements}
                maxHeight={announcementsHeight}
              />
            </div>

            {/* My Tickets Widget - Takes remaining space */}
            <div className="flex-1 min-h-0">
              <FreshdeskTicketsWidget
                tickets={freshdeskTickets}
                isLoading={isLoadingFreshdeskTickets}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
